// Premium, portal-rendered dropdown. Stripe-inspired: crisp borders,
// subtle depth, animated open/close, keyboard nav, checkmark on selected,
// search when the option list is long.
//
// API — drop-in replacement for a native <select>:
//   <PragmaSelect
//     value={pipelineId}
//     onChange={setPipelineId}
//     options={[{ value:"", label:"All pipelines" }, ...]}
//     placeholder="Select…"
//     testId="filter-pipeline"
//     size="md"                 // "sm" | "md"
//     searchable                // auto when >= 10 options
//     clearable                 // shows an × in the trigger
//     disabled
//     label="Pipeline"          // optional inline label above trigger
//   />
//
// Options can be flat `[{value,label,hint?,disabled?}]` OR grouped
// `[{group:"Growth", options:[{value,label}]}, ...]`.

import { forwardRef, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { ChevronDown, Check, Search, X } from "lucide-react";

const SEARCH_THRESHOLD = 10;

export default function PragmaSelect({
  value,
  onChange,
  options = [],
  placeholder = "Select…",
  testId,
  size = "md",
  searchable,
  clearable = false,
  disabled = false,
  label,
  className = "",
  style,
  menuMinWidth,
  emptyLabel = "No options",
  align = "left", // "left" | "right"
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);

  // Normalise options into a flat list of items and headers for rendering.
  const { flat, headers } = useMemo(() => normaliseOptions(options), [options]);
  const isSearchable = searchable ?? flat.length >= SEARCH_THRESHOLD;

  const filtered = useMemo(() => {
    if (!query) return flat;
    const q = query.toLowerCase();
    return flat.filter((o) =>
      (o.label || "").toLowerCase().includes(q) ||
      (o.hint || "").toLowerCase().includes(q)
    );
  }, [flat, query]);

  const selected = flat.find((o) => String(o.value) === String(value));

  // Positioning: compute once when opening, then again on scroll/resize.
  // `pos.flip` toggles between top-anchored (below trigger) and bottom-anchored
  // (above trigger) so the flipped menu hugs the trigger no matter how tall it is.
  const reposition = useCallback(() => {
    const t = triggerRef.current;
    if (!t) return;
    const rect = t.getBoundingClientRect();
    const width = Math.max(rect.width, menuMinWidth || 0);
    const left = align === "right"
      ? Math.max(8, rect.right - width)
      : Math.min(rect.left, window.innerWidth - width - 8);

    const menuMaxH = 320;
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    if (spaceBelow >= menuMaxH || spaceBelow >= spaceAbove) {
      // Open below: anchor to trigger.bottom
      setPos({
        flip: false,
        top: rect.bottom + 6,
        left,
        width,
        maxHeight: Math.max(120, Math.min(menuMaxH, spaceBelow)),
      });
    } else {
      // Open above: bottom-anchor to trigger.top so the panel hugs the control
      setPos({
        flip: true,
        bottom: window.innerHeight - rect.top + 6,
        left,
        width,
        maxHeight: Math.max(120, Math.min(menuMaxH, spaceAbove)),
      });
    }
  }, [align, menuMinWidth]);

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    const onScroll = () => reposition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, reposition]);

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Autofocus search when opening
  useEffect(() => {
    if (!open) { setQuery(""); setActiveIndex(-1); return; }
    // Focus search after paint if searchable
    const raf = requestAnimationFrame(() => {
      if (isSearchable) searchRef.current?.focus();
      const idx = filtered.findIndex((o) => String(o.value) === String(value));
      setActiveIndex(idx >= 0 ? idx : 0);
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Reset active index when filter changes
  useEffect(() => { setActiveIndex(0); }, [query]);

  const pick = (opt) => {
    if (!opt || opt.disabled) return;
    onChange?.(opt.value, opt);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (!open) {
      if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(e.key)) {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === "Escape") { e.preventDefault(); setOpen(false); triggerRef.current?.focus(); return; }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(filtered.length - 1, (i < 0 ? -1 : i) + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = filtered[activeIndex];
      if (opt) pick(opt);
    } else if (e.key === "Home") { setActiveIndex(0); }
    else if (e.key === "End")   { setActiveIndex(filtered.length - 1); }
  };

  const sizePad = size === "sm" ? "6px 10px 6px 12px" : "8px 12px 8px 14px";
  const sizeFont = size === "sm" ? 12 : 13;

  return (
    <div className={className} style={style}>
      {label && (
        <label className="mono text-[10px] uppercase tracking-wider" style={{ display: "block", color: "var(--pg-text-mute)", marginBottom: 6 }}>{label}</label>
      )}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onKeyDown}
        disabled={disabled}
        data-testid={testId}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          width: "100%",
          padding: sizePad,
          background: "var(--pg-surface, #fff)",
          border: `1px solid ${open ? "var(--pg-accent, #5B4FE8)" : "var(--pg-border)"}`,
          borderRadius: 8,
          fontSize: sizeFont,
          color: selected ? "var(--pg-text)" : "var(--pg-text-mute)",
          textAlign: "left",
          cursor: disabled ? "not-allowed" : "pointer",
          transition: "border-color 0.15s ease, box-shadow 0.15s ease",
          boxShadow: open ? "0 0 0 3px color-mix(in oklch, var(--pg-accent, #5B4FE8) 18%, transparent)" : "none",
          display: "flex", alignItems: "center", gap: 8,
          opacity: disabled ? 0.55 : 1,
          fontFamily: "inherit",
        }}
      >
        <span style={{
          flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          color: selected ? "var(--pg-text)" : "var(--pg-text-mute)",
        }}>
          {selected ? selected.label : placeholder}
        </span>
        {clearable && selected && selected.value && !disabled && (
          <span
            role="button"
            aria-label="Clear"
            data-testid={testId ? `${testId}-clear` : undefined}
            onClick={(e) => { e.stopPropagation(); onChange?.("", null); }}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 18, height: 18, borderRadius: 4,
              color: "var(--pg-text-mute)", cursor: "pointer",
              transition: "background 0.15s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--pg-surface-2)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <X size={12} strokeWidth={2.5} />
          </span>
        )}
        <ChevronDown
          size={14}
          strokeWidth={2}
          style={{
            color: "var(--pg-text-mute)",
            transition: "transform 0.18s ease",
            transform: open ? "rotate(180deg)" : "rotate(0)",
            flexShrink: 0,
          }}
        />
      </button>

      {open && ReactDOM.createPortal(
        <Menu
          ref={menuRef}
          pos={pos}
          isSearchable={isSearchable}
          query={query}
          setQuery={setQuery}
          searchRef={searchRef}
          filtered={filtered}
          value={value}
          headers={headers}
          activeIndex={activeIndex}
          setActiveIndex={setActiveIndex}
          onPick={pick}
          onKeyDown={onKeyDown}
          emptyLabel={emptyLabel}
        />,
        document.body
      )}
    </div>
  );
}

// eslint-disable-next-line react/display-name
const Menu = forwardRef(function Menu(
  { pos, isSearchable, query, setQuery, searchRef, filtered, value, headers, activeIndex, setActiveIndex, onPick, onKeyDown, emptyLabel },
  ref
) {
  return (
    <div
      ref={ref}
      role="listbox"
      onKeyDown={onKeyDown}
      style={{
        position: "fixed",
        ...(pos.flip
          ? { bottom: pos.bottom }
          : { top: pos.top }),
        left: pos.left,
        width: pos.width,
        maxHeight: pos.maxHeight || 320,
        background: "var(--pg-surface, #fff)",
        border: "1px solid var(--pg-border)",
        borderRadius: 10,
        boxShadow: "0 6px 24px rgba(0,0,0,0.10), 0 1px 3px rgba(0,0,0,0.04)",
        zIndex: 400,
        display: "flex", flexDirection: "column",
        overflow: "hidden",
        animation: pos.flip ? "pragmaSelectFadeUp 0.14s ease-out" : "pragmaSelectFade 0.14s ease-out",
        transformOrigin: pos.flip ? "bottom left" : "top left",
      }}
    >
      {isSearchable && (
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "8px 12px", borderBottom: "1px solid var(--pg-border)",
          background: "var(--pg-surface, #fff)",
        }}>
          <Search size={13} strokeWidth={2} style={{ color: "var(--pg-text-mute)" }} />
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            data-testid="pragma-select-search"
            style={{
              flex: 1, border: 0, outline: "none", background: "transparent",
              fontSize: 12.5, color: "var(--pg-text)",
              fontFamily: "inherit",
            }}
          />
        </div>
      )}
      <div style={{ overflow: "auto", padding: "4px 0" }}>
        {filtered.length === 0 && (
          <div style={{ padding: "10px 14px", fontSize: 12.5, color: "var(--pg-text-mute)" }}>{emptyLabel}</div>
        )}
        {filtered.map((opt, idx) => {
          const isSelected = String(opt.value) === String(value);
          const isActive = idx === activeIndex;
          const groupHeader = headers[opt.__id];
          return (
            <div key={`${opt.value}-${idx}`}>
              {groupHeader && (
                <div className="mono text-[10px] uppercase tracking-wider"
                     style={{ padding: "10px 14px 4px", color: "var(--pg-text-mute)" }}>
                  {groupHeader}
                </div>
              )}
              <button
                type="button"
                onMouseEnter={() => setActiveIndex(idx)}
                onClick={() => onPick(opt)}
                disabled={opt.disabled}
                data-testid={`pragma-select-option-${String(opt.value)}`}
                style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%",
                  padding: "8px 14px",
                  border: 0,
                  background: isActive ? "var(--pg-surface-2, rgba(0,0,0,0.03))" : "transparent",
                  color: opt.disabled ? "var(--pg-text-mute)" : "var(--pg-text)",
                  fontSize: 13,
                  textAlign: "left",
                  cursor: opt.disabled ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                  transition: "background 0.1s ease",
                }}
              >
                <span style={{ width: 14, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {isSelected && <Check size={13} strokeWidth={2.5} style={{ color: "var(--pg-accent, #5B4FE8)" }} />}
                </span>
                <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                  <span style={{
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    fontWeight: isSelected ? 600 : 400,
                  }}>{opt.label}</span>
                  {opt.hint && (
                    <span className="mono text-[10px]" style={{ color: "var(--pg-text-mute)" }}>{opt.hint}</span>
                  )}
                </span>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
});

// Given the (possibly grouped) options prop, return a flat array of items
// plus a map of headers keyed by the item's synthetic __id so we can render
// the group label above the first item in each group.
function normaliseOptions(options) {
  const flat = [];
  const headers = {};
  let id = 0;
  const isGrouped = options.some((o) => o && Array.isArray(o.options));
  if (isGrouped) {
    for (const g of options) {
      if (Array.isArray(g.options)) {
        g.options.forEach((o, i) => {
          const item = { ...o, __id: id++ };
          if (i === 0 && g.group) headers[item.__id] = g.group;
          flat.push(item);
        });
      } else {
        flat.push({ ...g, __id: id++ });
      }
    }
  } else {
    for (const o of options) flat.push({ ...o, __id: id++ });
  }
  return { flat, headers };
}
